/**
 * Build the CreateOrderRequestV2 payload for `api.postOrderV2`.
 * Kept pure for readability/testing: pass current state in, get payload out.
 */

export function mapApiContactMethod(raw) {
    const m = raw != null ? String(raw).toLowerCase().trim() : ""
    if (m === "email" || m === "phone" || m === "text") {
        return m
    }
    return "phone"
}

/** Verbatim API shape for `ship_to` / `bill_to` (CreateOrderRequestV2). */
function buildAddressPayload(addr) {
    return {
        first_name: addr.firstName,
        last_name: addr.lastName,
        company: addr.companyName,
        street_address: addr.streetAddress,
        city: addr.city,
        state: addr.province,
        zip: addr.postalCode,
        country: addr.country,
        phone: addr.phone,
        email: addr.email,
    }
}

function buildProcessingDetails(processing) {
    return (processing.instructions ?? []).map((row) => ({
        process_id: row.processId ?? null,
        species_id: row.speciesId ?? null,
        num_fish: row.quantity === "" || row.quantity == null ? null : Number(row.quantity),
        unit_type:
            row.unit === "Whole Fish" ? "whole"
                : row.unit === "Individual Pieces" ? "pieces"
                    : null,
        fish_description: String(row.additionalNotes ?? "").trim(),
    }))
}

function buildProcessingOptions(processing) {
    return Object.entries(processing.options ?? {})
        .filter(([, v]) => Boolean(v))
        .map(([k, v]) => ({
            option_id: Number(k),
            value: Boolean(v),
        }))
}

function buildSrcIds({ shipDelivery, billSameAsShip, shipping, billing, attachShip, attachBill }) {
    if (!shipDelivery) {
        return { src_ship_id: null, src_bill_id: null }
    }

    // When attaching/updating the customer default, omit src ids so FindOrCreateAddress runs.
    if (attachShip) {
        const src_bill_id =
            billSameAsShip || attachBill ? null :
                (billing.billingCustomerId != null && billing.defaultBillAddressId != null ?
                    Number(billing.defaultBillAddressId) :
                    null)
        return {
            src_ship_id: null,
            src_bill_id: src_bill_id != null && Number.isFinite(src_bill_id) ? src_bill_id : null,
        }
    }

    const shipSrc =
        shipping.shippingCustomerId != null && shipping.defaultShipAddressId != null ?
            Number(shipping.defaultShipAddressId) :
            null
    const src_ship_id = shipSrc != null && Number.isFinite(shipSrc) ? shipSrc : null

    if (billSameAsShip) {
        return { src_ship_id, src_bill_id: src_ship_id }
    }

    if (attachBill) {
        return { src_ship_id, src_bill_id: null }
    }

    const billSrc =
        billing.billingCustomerId != null && billing.defaultBillAddressId != null ?
            Number(billing.defaultBillAddressId) :
            null
    const src_bill_id = billSrc != null && Number.isFinite(billSrc) ? billSrc : null
    return { src_ship_id, src_bill_id }
}

export function buildOrderPayload({ app, contact, delivery, shipping, billing, dropoff, processing }) {
    const shipDelivery = delivery.method === "ship"
    const pickup = delivery.method === "pickup"
    const billSameAsShip = Boolean(billing.useShippingAddress)
    const holdForPickup = pickup ? Boolean(delivery.holdForPickup) : false

    const ship_to = shipDelivery ? buildAddressPayload(shipping) : null
    const bill_to =
        shipDelivery ?
            (billSameAsShip ? { ...ship_to } : buildAddressPayload(billing)) :
            buildAddressPayload(billing)

    const details = holdForPickup ? [] : buildProcessingDetails(processing)

    const ship_only =
        !holdForPickup &&
        details.length > 0 &&
        details.every((d) => Number(d.process_id) === 10)

    const options = holdForPickup ? [] : buildProcessingOptions(processing)

    const prepaidAmountNum = Number(contact.prepaidAmount ?? 0)

    // Explicit true required — UI defaults: yes for set-as-default, no for update-default.
    const attachShip = shipDelivery && shipping.attachAddressToCustomer === true
    const attachBill =
        billSameAsShip && shipDelivery ?
            attachShip :
            billing.attachAddressToCustomer === true

    const { src_ship_id, src_bill_id } = buildSrcIds({
        shipDelivery,
        billSameAsShip,
        shipping,
        billing,
        attachShip,
        attachBill,
    })

    return {
        customer_id: contact.customerId ?? null,
        first_name: contact.firstName,
        last_name: contact.lastName,
        email: contact.email,
        phone: contact.phone,
        company: contact.companyName,
        location_id:
            dropoff.locationId != null ?
                Number(dropoff.locationId) :
                app.locationId != null ?
                    Number(app.locationId) :
                    null,
        ordered_at: dropoff.date || null,
        fishing_license: contact.licenseNumber,
        license_front_img: contact.licenseFrontImg ?? null,
        license_back_img: contact.licenseBackImg ?? null,
        prepaid_amount: Number.isFinite(prepaidAmountNum) ? prepaidAmountNum : 0,
        trip_code: contact.tripCode,
        fish_tag: contact.fishTag,
        is_staff: Boolean(contact.is_staff),
        contact_method: mapApiContactMethod(contact.contact_method),
        subscribe_newsletter: Boolean(contact.subscribe_newsletter),
        update_customer_fields: Boolean(contact.customerId) && contact.updateCustomerFields === true,

        pick_up_location_id: pickup ? delivery.pickupLocationId ?? null : null,
        hold_for_pick_up: holdForPickup,
        ship_only: Boolean(ship_only),

        ship_to,
        src_ship_id,
        src_bill_id,
        bill_same_as_ship: pickup ? false : billSameAsShip,
        bill_to,
        attach_ship_address_to_customer: attachShip,
        attach_bill_address_to_customer: attachBill,

        total_boxes:
            processing.containers === "" || processing.containers == null ?
                null :
                Number(processing.containers),
        total_weight_lb:
            holdForPickup ?
                0 :
                processing.totalWeight === "" || processing.totalWeight == null ?
                    0 :
                    Number(processing.totalWeight),
        fresh: false,
        frozen: holdForPickup ? false : true,

        details,
        options,
    }
}

